/**
 * 他者による更新 / 削除の判定 (Issue #765)。
 *
 * 編集画面を開いた時点の更新日時 (`updated_at` = 「LastUpdate」) を保持しておき、更新確定の直前に
 * サーバから取り直した最新のデータと比べる。
 *
 *   - 最新のデータが無い                 → `deleted`   (他者が削除した)
 *   - 更新日時が開いたときと違う         → `modified`  (他者が更新した)
 *   - 更新日時が開いたときと同じ         → `unchanged`
 *
 * 更新日時が無い (null / undefined / 空文字) ものは「未設定」として同じ扱いにする。
 */
export type RemoteChangeState = 'unchanged' | 'modified' | 'deleted';

/** 更新日時を比較用に正規化する (null / undefined / 空文字 は同一視)。 */
function normalizeVersion(value: string | null | undefined): string {
  return typeof value === 'string' ? value : '';
}

/**
 * 開いたときの更新日時 (`baseline`) と、サーバから取り直した最新のデータ (`current`) を比べる。
 *
 * @param baseline 画面を開いた時点の `updated_at`
 * @param current  最新のデータ。存在しない (削除済み) ときは `undefined` / `null`
 */
export function judgeRemoteChange(
  baseline: string | null | undefined,
  current: { updated_at?: string | null } | null | undefined,
): RemoteChangeState {
  if (current === null || current === undefined) return 'deleted';
  return normalizeVersion(current.updated_at) === normalizeVersion(baseline)
    ? 'unchanged'
    : 'modified';
}

/**
 * 値そのものを比べる版 (更新日時を持たないデータ向け)。
 * 編集対象の値が、開いたときから変わっていれば `modified`。
 */
export function judgeRemoteValueChange(
  baseline: string | null | undefined,
  current: { value: string | null | undefined } | null | undefined,
): RemoteChangeState {
  if (current === null || current === undefined) return 'deleted';
  return normalizeVersion(current.value).trim() === normalizeVersion(baseline).trim()
    ? 'unchanged'
    : 'modified';
}

/**
 * オブジェクトをキー順に依存しない文字列にする (スナップショット比較用)。
 *
 * 放送地点のように `updated_at` を持たないデータでは、開いたときの内容全体の指紋を LastUpdate の代わりに保持し、
 * 更新確定時に取り直した最新の内容の指紋と比べる。`undefined` の値はキーごと無視する。
 */
export function fingerprint(value: unknown): string {
  return JSON.stringify(sortKeys(value)) ?? '';
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const v = (value as Record<string, unknown>)[key];
      if (v !== undefined) out[key] = sortKeys(v);
    }
    return out;
  }
  return value;
}
